<template>
  <div class="mt-5">
    <div class="mb-8">
      <div
        class="inline-flex items-center px-3 py-1 rounded-full bg-primary-light text-primary-dark text-xs font-semibold mb-4"
      >
        <span class="w-2 h-2 bg-primary-dark rounded-full mr-2 animate-pulse"></span>
        {{ t("starting_badge") }}
      </div>
      <h1 class="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 leading-tight md:leading-10">
        {{ t("who_takes_quiz") }}
      </h1>
      <p v-if="quiz.description" class="mt-3 text-sm text-gray-600">{{ quiz.description }}</p>
    </div>

    <div class="mt-8">
      <OfflineSellerCombobox
        ref="sellerCombobox"
        :sellerCompleteName="localSellerCompleteName"
        @selectedSeller="handleSelectSeller"
      />
    </div>

    <transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="selectedSeller" class="mt-6">
        <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-xs">
          <div>
            <p class="text-sm text-gray-500">{{ t("welcome") }}</p>
            <h2 class="mt-1 text-lg font-bold text-gray-900">{{ t("greeting", { name: selectedSeller.name }) }}</h2>
            <p class="mt-2 text-sm text-gray-600">
              {{ t("encouragement") }}
            </p>
          </div>

          <div class="mt-4">
            <div class="rounded-xl bg-gray-50 p-3 grid grid-cols-2 gap-3">
              <div>
                <p class="text-xs font-semibold text-gray-700">{{ t("duration_label") }}</p>
                <p class="mt-1 text-sm text-gray-600">{{ t("duration_value") }}</p>
              </div>
              <div>
                <p class="text-xs font-semibold text-gray-700">{{ t("validation_label") }}</p>
                <p class="mt-1 text-sm text-gray-600">{{ t("validation_value", { count: requiredCorrectAnswers }) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <div class="mt-10 flex w-full items-center justify-end">
      <GenericButton v-if="selectedSeller" @click="handleNext" class="w-auto px-6 py-2.5 transition-all duration-200">
        {{ isLastQuestion ? t("finish_quiz") : t("next") }}
      </GenericButton>
    </div>
  </div>
</template>

<script>
import { GenericButton } from "@estaly/ui";
import OfflineSellerCombobox from "@/components/combobox/OfflineSellerCombobox.vue";
import { mapState } from "vuex";
import { useI18n } from "vue-i18n";
import { QUIZ_PASSING_THRESHOLD } from "../constants";

export default {
  components: {
    GenericButton,
    OfflineSellerCombobox,
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  props: {
    componentStep: {
      type: Number,
      required: true,
    },
    currentStep: {
      type: Number,
      required: true,
    },
    totalQuestions: {
      type: Number,
      required: false,
    },
    initialSeller: {
      type: Object,
      default: null,
    },
    quiz: {
      type: Object,
      required: true,
    },
  },
  emits: ["next", "previous"],
  data() {
    return {
      selectedSeller: this.initialSeller,
      localSellerCompleteName: this.initialSeller?.name || "",
    };
  },
  computed: {
    ...mapState("sellers", ["index"]),
    questionsCount() {
      return this.quiz.questions?.length || 0;
    },
    requiredCorrectAnswers() {
      return Math.ceil(this.questionsCount * QUIZ_PASSING_THRESHOLD);
    },
    isLastQuestion() {
      return this.componentStep === this.totalQuestions;
    },
  },
  watch: {
    index: {
      handler() {
        this.syncSellerFromIndex();
      },
      immediate: true,
    },
    initialSeller(newValue) {
      if (newValue) {
        this.selectedSeller = newValue;
        this.localSellerCompleteName = newValue.name || "";
        this.syncSellerFromIndex();
      }
    },
  },
  mounted() {
    this.syncSellerFromIndex();
  },
  methods: {
    syncSellerFromIndex() {
      if (!this.initialSeller?.id || !this.index?.length) return;
      const foundSeller = this.index.find((s) => s.id === this.initialSeller.id);
      if (!foundSeller) return;

      this.selectedSeller = foundSeller;
      this.localSellerCompleteName = foundSeller.name;

      if (this.$refs.sellerCombobox) {
        this.$nextTick(() => {
          this.$refs.sellerCombobox.selectedSeller = foundSeller;
        });
      }
    },
    handleSelectSeller(seller) {
      this.selectedSeller = seller;
      this.localSellerCompleteName = seller?.name || "";
    },
    handleNext() {
      if (this.selectedSeller) {
        this.$emit("next", { seller: this.selectedSeller });
      }
    },
  },
};
</script>

<i18n src="./SellerSelectionStep.i18n.json"></i18n>
